import { useEffect, useState } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Tag, notification } from 'antd';
import crypto from 'crypto';
import { roles } from '../enum/roleType';
import pending from '../../assets/pending.svg';
import running from '../../assets/running.svg';
import styles from '../helpers/global.module.css';
import {
  configEncryptionDecryption,
  conversionAmount,
  passwordValidationPattern,
  strengthLevels,
} from '../../constants/app.constants';
import CONSTANTS from '../../constants/api.constants';
import { Api } from '../api/api';
import { agentScheduleTabTypes, timeBasedAgentScheduleTypes, tabType } from '../enum/agentTabs';
import store from '../../redux/store';

export const pushToLink = (history, link, state = {}) => {
  history.push({ pathname: link, state });
};

export const getWindowDimension = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return { width, height };
};
export const findParametersHavingValue = (data) => {
  Object.keys(data).forEach(function (key) {
    if (
      typeof data[key] === 'undefined' ||
      data[key] === 'id' ||
      data[key] === '' ||
      data[key] === null
    ) {
      delete data[key];
    }
  });
  return data;
};
export const useDimensions = () => {
  const [dimensions, setDimensions] = useState(getWindowDimension());

  useEffect(() => {
    const handleDimensionChange = () => setDimensions(getWindowDimension());

    window.addEventListener('resize', handleDimensionChange);
    return () => window.removeEventListener('resize', handleDimensionChange);
  });
  return dimensions;
};

export const getStatus = (status) => {
  switch (status) {
    case 0:
      return (
        <div
          className={styles.statusWrapper}
          style={{ backgroundColor: 'var(--pending-bg-color)' }}
        >
          Pending
        </div>
      );
    case 1:
      return (
        <div
          className={styles.statusWrapper}
          style={{ backgroundColor: 'var(--running-bg-color)' }}
        >
          Running
        </div>
      );
    case 2:
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Executed
        </Tag>
      );
    case 3:
      return (
        <Tag icon={<CloseCircleOutlined />} color="error">
          Error
        </Tag>
      );
  }
};
export function hashText(text) {
  return btoa(text);
}
export function copyToClipboard(text) {
  var input = document.createElement('input');
  input.setAttribute('value', text);
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
}

export const openNotification = (message = '', description = '') => {
  notification.open({
    message,
    description,
    type: 'info',
    style: { width: '300px', fontSize: '12px' },
  });
};

export const isAdmin = () => {
  return getUserRole() === roles.SuperAdmin;
};
export const isSubAdmin = () => {
  return getUserRole() === roles.SubAdmin;
};
export const isBotCreator = (botId) => {
  return getUserId() === botId;
};
export const isCustomerAdmin = () => {
  return getUserRole() === roles.CustomerAdmin;
};
export const isUser = () => {
  return getUserRole() === roles.User;
};

export const getUserRole = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).role;
  }
};
export const getUserId = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).id;
  }
};
export const isAdminOrBotCreator = (userId) => {
  if (isAdmin() || isSubAdmin() || isBotCreator(userId)) {
    return true;
  }
  return false;
};
export const getRoleId = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).roleId;
  }
};
export const getUserEmail = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).email;
  }
};

export const getCompanyId = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).companyId;
  }
};
export const getCompanyName = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).companyName;
  }
};
export const getFullName = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).fullName;
  }
};

export const resObject = (type, operation, message, data) => {
  return {
    content: {
      type,
      operation,
      message,
      data,
    },
  };
};

export const downloadFile = async (url, filename) => {
  return new Promise((resolve, reject) => {
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.setAttribute('download', filename);
      setTimeout(() => {
        anchor.click();
        resolve(true);
      }, 1000);
    } catch (e) {
      reject(false);
    }
  });
};

export const findCustomerAdmin = (data) => {
  const customerAdmin = data.filter((role) => {
    return role.name === roles.CustomerAdmin;
  });
  return customerAdmin[0];
};

export const isAlphanumeric = (stringValue) => {
  return /^[a-zA-Z0-9\s]+$/.test(stringValue);
};

export const isEnabledEmailSchedule = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user')).enabledEmailSchedule;
  }
};

export const getAssociatedToGroups = () => {
  if (localStorage.getItem('user')) {
    return JSON.parse(localStorage.getItem('user'))?.associatedToGroups;
  }
};
export const isValidEmailAddress = (email) => {
  const emailRegex = /^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/;
  if (emailRegex.test(email)) {
    let emailTLD = email.split('@')[1];
    emailTLD = emailTLD?.split('.')?.slice(1);
    return emailTLD.every((tld) => ['edu', 'in', 'com', 'gov', 'org', 'net'].includes(tld));
  } else {
    return false;
  }
};

export const encrypt = async (text) => {
  const algorithm = configEncryptionDecryption.ALGORITHM;
  const password = configEncryptionDecryption.SECRET;
  const iv = crypto.randomBytes(16);
  const cipher = crypto.createCipheriv(algorithm, password, iv);
  let crypted = cipher.update(text, 'utf8', 'hex');
  crypted += cipher.final('hex');
  return iv.toString('hex') + ':' + crypted;
};

export const decrypt = (text) => {
  const algorithm = configEncryptionDecryption.ALGORITHM;
  const password = configEncryptionDecryption.SECRET;
  const [ivHex, encryptedHex] = text.split(':');
  const iv = Buffer.from(ivHex, 'hex');
  const decipher = crypto.createDecipheriv(algorithm, password, iv);
  let dec = decipher.update(encryptedHex, 'hex', 'utf8');
  dec += decipher.final('utf8');
  return dec;
};

const getConversionRate = () => {
  return store.getState().rateConversion?.data.conversionAmount;
};


export const priceConversion = (price, currency) => {
  const inrPrice = price || 0;
  const convertedPrice =
    currency === 'INR' ? Math.round(Number(inrPrice)) : (inrPrice / conversionAmount).toFixed(2);
  return `${convertedPrice}`;
};

export const getPriceINRToUSD = (inrPrice = 0) => {
  return parseFloat((inrPrice / getConversionRate()).toFixed(2));
};

export const getPriceUSDtoINR = (usdPrice = 0) => {
  return parseFloat((usdPrice * getConversionRate()).toFixed(2));
};



export const getAgentTabs = () => {
  const tabs = [
    {
      label: 'Agents',
      tabKey: tabType.agents,
    },
  ];
  if (!isSubAdmin() && !isUser()) {
    tabs.push({
      label: 'Tokens',
      tabKey: tabType.tokens,
    });
  }
  return tabs;
};

export const getProfileTabs = (isEmailConfigTab = true) => {
  const tabs = [
    {
      label: 'Overview',
      tabKey: tabType.profile,
    },
  ];
  if (isEmailConfigTab) {
    tabs.push({
      label: 'E-mail Configuration',
      tabKey: tabType.emailConfiguration,
    });
  }
  return tabs;
};

export const getManageUsersTabs = (isCompany = true, isUsers = true, isGroups = true) => {
  const tabs = [];
  if (isCompany) {
    tabs.push({
      label: 'Companies',
      tabKey: tabType.companies,
    });
  }
  if (isUsers) {
    tabs.push({
      label: 'Users',
      tabKey: tabType.users,
    });
  }
  if (isGroups) {
    tabs.push({
      label: 'Groups',
      tabKey: tabType.groups,
    });
  }
  return tabs;
};

export const getAgentScheduleTabs = (isEmailTab = false) => {
  const tabs = [
    {
      label: 'Time Based',
      value: agentScheduleTabTypes.timeBased,
    },
  ];
  if (isEmailTab) {
    tabs.push({
      label: 'E-mail Based',
      value: agentScheduleTabTypes.emailBased,
    });
  }
  return tabs;
};

export const getTimeBasedAgentScheduleTabs = () => {
  const tabs = [
    {
      label: 'Run Once',
      tabKey: timeBasedAgentScheduleTypes.runOnce,
    },
    {
      label: 'Run Repeatedly',
      tabKey: timeBasedAgentScheduleTypes.runRepeatedly,
    },
  ];
  return tabs;
};

export const getPasswordStrength = (password) => {
  let score = 0;
  if (passwordValidationPattern.minLengthRegExp.test(password)) score++;
  if (passwordValidationPattern.lowercaseRegExp.test(password)) score++; // lowercase
  if (passwordValidationPattern.uppercaseRegExp.test(password)) score++; // uppercase
  if (passwordValidationPattern.digitsRegExp.test(password)) score++; // digit
  if (passwordValidationPattern.specialCharRegExp.test(password)) score++; // special char
  if (!passwordValidationPattern.spaces.test(password)) return strengthLevels[Math.min(score, 4)];
  return strengthLevels[Math.min(score, 5)];
};

export const getQueryParams = (params) => {
  const obj = findParametersHavingValue(params);
  const entries = Object.entries(obj);
  if (entries.length === 0) return '';
  return (
    '?' +
    entries
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')
  );
};

export const calculatePageSize = (availableHeight, rowHeight = 1) => {
  const minRows = 5;
  if (!availableHeight || availableHeight <= 0) return minRows;
  return Math.floor(availableHeight / rowHeight);
};

export const getSingleBotDetails = async (botId) => {
  try {
    const data = await Api.get(`${CONSTANTS.baseUrl}${CONSTANTS.getBotDetails}/${botId}`);
    if (data.status === 200)
      return {data: data.data.payload, error: null};
  } catch (error) {
    if (error.response && error.response.status === 400)
      return {data: null, error: error.response.data.message};
    else return {data:null, error: 'Failed to fetch bot details. Please try again later!'};
  }
};